// components/Admin/BotControl.tsx
//
// The host's "second screen". Define a roster of believable players, arm them
// against the live session, then narrate the feed below while the main game runs
// from SessionManager. Bots ride the human write paths (see bot-engine.ts), so
// nothing here touches the session topology — it only reads the quiz/answer key
// once (to prime the engine) and subscribes to the same tiny session delta the
// SessionManager already uses.
import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Quiz, SessionState, LeaderboardEntry } from '../../types/quiz.types';
import {
  getQuiz,
  getCorrectAnswers,
  subscribeToSession,
  subscribeToLeaderboard,
  publishLeaderboard,
  serverNow,
} from '../../services/live-session';
import { BotRunner, generateRoster, Bot, BotStatus, Tier } from '../../services/bot-engine';
import { rankChip } from '../../constants/answer-tiles';

const TIER_META: Record<Tier, { label: string; hint: string; dot: string }> = {
  sharp: { label: 'Sharp', hint: '~90% right, fast', dot: 'bg-tile-green' },
  average: { label: 'Average', hint: '~60% right, mid', dot: 'bg-tile-gold' },
  casual: { label: 'Casual', hint: '~33% right, slow', dot: 'bg-tile-red' },
};

// One-tap crowd sizes so the host isn't fiddling with three number inputs
// minutes before going live.
const PRESETS: Array<{ label: string; counts: Record<Tier, number> }> = [
  { label: 'Small · 8', counts: { sharp: 2, average: 4, casual: 2 } },
  { label: 'Medium · 17', counts: { sharp: 5, average: 8, casual: 4 } },
  { label: 'Packed · 30', counts: { sharp: 8, average: 14, casual: 8 } },
];

export const BotControl = () => {
  const { sessionCode } = useParams<{ sessionCode: string }>();
  const [session, setSession] = useState<SessionState | null>(null);
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [correct, setCorrect] = useState<number[]>([]);
  const [ranks, setRanks] = useState<LeaderboardEntry[]>([]);

  const [counts, setCounts] = useState<Record<Tier, number>>({ sharp: 5, average: 8, casual: 4 });
  const [roster, setRoster] = useState<Bot[]>([]);
  const [armed, setArmed] = useState(false);
  const [disarming, setDisarming] = useState(false);
  const [now, setNow] = useState(serverNow());

  const runnerRef = useRef<BotRunner | null>(null);
  const [, force] = useState(0); // bumped by the runner when statuses mutate

  // One-time reads: quiz blob (timers/options) + answer key (to prime the engine).
  useEffect(() => {
    if (!session?.quizId || quiz) return;
    getQuiz(session.quizId).then(setQuiz);
    getCorrectAnswers(session.quizId).then(setCorrect);
  }, [session?.quizId, quiz]);

  // Single session + leaderboard subscription; feeds the runner on every delta.
  useEffect(() => {
    if (!sessionCode) return;
    const unsubSession = subscribeToSession(sessionCode, (s) => {
      setSession(s);
      runnerRef.current?.onSession(s);
    });
    const unsubRanks = subscribeToLeaderboard(sessionCode, setRanks);
    return () => { unsubSession(); unsubRanks(); runnerRef.current?.stop(); };
  }, [sessionCode]);

  // Countdown ticker, only while a question is live.
  useEffect(() => {
    if (session?.status !== 'in_progress') return;
    const id = setInterval(() => setNow(serverNow()), 250);
    return () => clearInterval(id);
  }, [session?.status, session?.currentQuestionIndex]);

  // Bots run entirely in this tab — closing/reloading it kills them mid-game.
  useEffect(() => {
    if (!armed) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [armed]);

  const total = counts.sharp + counts.average + counts.casual;

  const handleGenerate = () => setRoster(generateRoster(counts));

  const handleArm = () => {
    if (!sessionCode || !quiz) return;
    const r = roster.length ? roster : generateRoster(counts);
    setRoster(r);
    const runner = new BotRunner(sessionCode, r, () => force((n) => n + 1));
    runner.prime(quiz, correct);
    runner.seedLobby();
    runner.onSession(session); // catch up if the game is already running
    runnerRef.current = runner;
    setArmed(true);
  };

  const handleDisarm = async () => {
    const r = runnerRef.current;
    if (!r || disarming) return;
    setDisarming(true);
    try {
      r.stop();
      await r.removeFromRtdb();
      // Removing answers/participants doesn't touch the already-published board —
      // recompute it so disarm leaves no bot names (or inflated totalPlayers) behind.
      if (sessionCode && session?.quizId) await publishLeaderboard(sessionCode, session.quizId);
      runnerRef.current = null;
      setArmed(false);
    } finally {
      setDisarming(false);
    }
  };

  const statuses: BotStatus[] = runnerRef.current?.getStatuses() ?? [];
  const present = statuses.filter((s) => s.state !== 'pending_join');
  const answered = present.filter((s) => s.state === 'answered');

  const isStream = !!session?.youtubeUrl;
  const idx = session?.currentQuestionIndex ?? 0;
  const currentQ = quiz?.questions[idx];
  // Stream mode has no meaningful timer (the host paces the window by hand), so
  // the countdown only exists in app mode.
  const remaining =
    !isStream && session?.status === 'in_progress' && currentQ
      ? Math.max(0, currentQ.timer - (now - session.questionStartTime) / 1000)
      : null;

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold font-display tracking-tight">Bot control</h1>
          <p className="text-sm text-slate-500">
            Session <span className="font-display font-bold tabular-nums">{sessionCode}</span> · host-only second screen
          </p>
        </div>
        {/* New tab on purpose: the bots live in THIS tab — navigating away kills them. */}
        <Link
          to={`/admin/session/${sessionCode}`}
          target="_blank"
          rel="noreferrer"
          className="btn-ghost text-sm !text-ink !bg-ink/10 hover:!bg-ink/15"
        >
          Game controls ↗
        </Link>
      </div>

      {/* Roster setup (pre-arm) */}
      {!armed && (
        <div className="card p-5 space-y-4">
          <h3 className="font-bold">Roster</h3>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                onClick={() => { setCounts(p.counts); setRoster([]); }}
                className="px-3 py-1.5 rounded-full text-sm font-semibold bg-ink/5 hover:bg-ink/10 tabular-nums"
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3">
            {(Object.keys(TIER_META) as Tier[]).map((tier) => (
              <label key={tier} className="block">
                <span className="flex items-center gap-2 text-sm font-semibold">
                  <span className={`w-2.5 h-2.5 rounded-full ${TIER_META[tier].dot}`} />
                  {TIER_META[tier].label}
                </span>
                <span className="text-xs text-slate-400">{TIER_META[tier].hint}</span>
                <input
                  type="number"
                  min={0}
                  value={counts[tier]}
                  onChange={(e) =>
                    setCounts((c) => ({ ...c, [tier]: Math.max(0, Number(e.target.value) || 0) }))
                  }
                  className="mt-1 w-full p-2 rounded-lg border-2 border-ink/15 focus:border-ink focus:outline-none tabular-nums"
                />
              </label>
            ))}
          </div>

          <div className="flex gap-3">
            <button onClick={handleGenerate} className="btn-ghost text-sm !text-ink !bg-ink/10 hover:!bg-ink/15" disabled={total === 0}>
              Preview names
            </button>
            <button onClick={handleArm} className="btn-primary flex-1" disabled={total === 0 || !quiz}>
              Arm {total} bot{total === 1 ? '' : 's'}
            </button>
          </div>

          <p className="text-xs text-slate-400">
            Bots run from this tab — keep it open while the game runs.
            {isStream && ' Stream mode: they answer only while the answer window is open, like everyone else.'}
          </p>

          {roster.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {roster.map((b) => (
                <span key={b.uid} className="px-2.5 py-1 rounded-full bg-ink/10 text-sm flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${TIER_META[b.tier].dot}`} />
                  {b.name}
                </span>
              ))}
            </div>
          )}
          {!quiz && session && <p className="text-xs text-slate-400">Loading quiz…</p>}
        </div>
      )}

      {/* Live narration feed (armed) */}
      {armed && (
        <>
          <div className="card p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold">
                {session?.status === 'not_started' && `Lobby · ${present.length}/${roster.length} joined`}
                {session?.status === 'in_progress' && `Q${idx + 1} · ${answered.length}/${present.length} answered`}
                {session?.status === 'ended' && 'Game ended'}
              </h3>
              {isStream && session?.status === 'in_progress' ? (
                <span className={`chip ${session.answersOpen ? 'bg-tile-green text-white' : 'bg-ink/10 text-ink/60'}`}>
                  {session.answersOpen ? 'OPEN' : 'LOCKED'}
                </span>
              ) : remaining !== null ? (
                <span className="font-display font-bold tabular-nums text-ink">{remaining.toFixed(0)}s</span>
              ) : null}
            </div>

            {/* Host-only answer key for the live question — narration aid */}
            {session?.status === 'in_progress' && currentQ && (
              <div className="mb-4 text-sm">
                <p className="font-medium">{currentQ.questionText}</p>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {currentQ.options.map((opt, i) => (
                    <span
                      key={i}
                      className={`px-2 py-0.5 rounded text-xs ${
                        i === correct[idx] ? 'bg-tile-green/10 text-tile-green-deep font-semibold' : 'bg-ink/5 text-ink/60'
                      }`}
                    >
                      {opt}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {[...present]
                .sort((a, b) => (a.answeredAtMs ?? Infinity) - (b.answeredAtMs ?? Infinity))
                .map((s) => (
                  <span
                    key={s.uid}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 ${
                      s.state === 'answered'
                        ? s.correct === false
                          ? 'bg-tile-red/10 text-tile-red-deep'
                          : 'bg-tile-green/10 text-tile-green-deep'
                        : s.state === 'thinking'
                        ? 'bg-accent/20 text-ink animate-pulse'
                        : 'bg-ink/5 text-ink/50'
                    }`}
                  >
                    {s.name}
                    {s.state === 'thinking' && ' …'}
                    {s.state === 'answered' && (s.correct === false ? ' ✗' : s.correct ? ' ✓' : ' •')}
                  </span>
                ))}
            </div>
          </div>

          <div className="card p-5">
            <h3 className="font-bold mb-3">Leaderboard (top {ranks.length})</h3>
            {ranks.length === 0 ? (
              <p className="text-slate-400 text-sm">Scores appear once the host advances a question.</p>
            ) : (
              <ol className="space-y-1.5">
                {ranks.map((e, i) => (
                  <li key={e.uid} className="flex items-center gap-3 text-sm">
                    <span className={rankChip(i)}>{i + 1}</span>
                    <span className="flex-1 font-medium truncate">{e.name}</span>
                    <span className="font-bold font-display text-ink tabular-nums">{e.score}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>

          <button onClick={handleDisarm} disabled={disarming} className="btn-danger w-full">
            {disarming ? 'Removing bots…' : 'Disarm & remove bots'}
          </button>
        </>
      )}
    </div>
  );
};
